/**
 * TaskDetailOffcanvas - Combined Offcanvas components for Task Detail page
 * 
 * Contains:
 * - FilterTaskActivityOffcanvas: Filter for activity timeline
 * - ViewHistoryOffcanvas: View task history (time log, comments, activities)
 */

import React, { useState } from 'react';
import { Offcanvas, Form, Row, Col, Button, Dropdown, Nav } from 'react-bootstrap';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';

/**
 * FilterTaskActivityOffcanvas - Filter for Task Activity Timeline
 */
export const FilterTaskActivityOffcanvas = ({ 
    show, 
    onHide,
    activityDateRange,
    setActivityDateRange,
    activityTypes,
    setActivityTypes,
    activityUsers,
    setActivityUsers,
    onApply,
    onClear
}) => {
    const activityTypeOptions = [
        { value: 'status', label: 'Status Changes' },
        { value: 'comment', label: 'Comments' },
        { value: 'file', label: 'File Uploads' },
        { value: 'priority', label: 'Priority Updates' },
        { value: 'assignment', label: 'Assignments' },
        { value: 'subtask', label: 'Subtask Changes' }
    ];

    const userOptions = [
        { value: '1', label: 'Sarah Wilson' },
        { value: '2', label: 'Mike Johnson' },
        { value: '3', label: 'John Doe' },
        { value: '4', label: 'Jane Smith' },
        { value: '5', label: 'David Brown' }
    ];

    const handleReset = () => {
        setActivityDateRange(null);
        setActivityTypes([]);
        setActivityUsers([]);
        if (onClear) onClear();
    };

    const handleApply = () => {
        if (onApply) onApply();
        onHide();
    };

    return (
        <Offcanvas show={show} onHide={onHide} placement="end">
            <Offcanvas.Header className='px-4 py-3' closeButton>
                <Offcanvas.Title className='display-1'>Filter Activity</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className='px-4 py-3'>
                <Form>
                    <Row className='gy-2'>
                        {/* Date Range */}
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Date Range</Form.Label>
                                <Flatpickr
                                    value={activityDateRange}
                                    onChange={(dates) => setActivityDateRange(dates)}
                                    className="form-control"
                                    options={{
                                        mode: 'range',
                                        dateFormat: 'd M Y',
                                        allowInput: true,
                                        disableMobile: true
                                    }}
                                    placeholder="Select date range"
                                />
                            </Form.Group>
                        </Col>

                        {/* Activity Types */}
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Activity Types</Form.Label>
                                <Select
                                    value={activityTypes}
                                    onChange={setActivityTypes}
                                    options={activityTypeOptions}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Select activity types"
                                    isClearable
                                    isMulti
                                />
                            </Form.Group>
                        </Col>

                        {/* Users */}
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Users</Form.Label>
                                <Select
                                    value={activityUsers}
                                    onChange={setActivityUsers}
                                    options={userOptions}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Select users"
                                    isClearable
                                    isMulti
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Offcanvas.Body>
            <div className="p-3 border-top">
                <div className="d-flex justify-content-between">
                    <Button variant="link" className='text-decoration-none' onClick={handleReset}>
                        Reset
                    </Button>
                    <Button variant="primary" onClick={handleApply}>
                        Apply Filters
                    </Button>
                </div>
            </div>
        </Offcanvas>
    );
};

/**
 * ViewHistoryOffcanvas - View task history
 */
export const ViewHistoryOffcanvas = ({ show, onHide, taskData }) => {
    const [activeTab, setActiveTab] = useState('timeLog');
    const [sortBy, setSortBy] = useState('newest');

    // Sample history data - replace with actual data from props or API
    const historyData = {
        timeLog: [
            {
                id: 1,
                date: '2025-08-06',
                startTime: '9:00 AM',
                endTime: '11:30 AM',
                duration: '2h 30m',
                description: 'Working on API integration and testing',
                billable: true,
                loggedBy: 'John Doe',
                loggedAt: '2025-08-06 11:35 AM'
            },
            {
                id: 2,
                date: '2025-08-05',
                startTime: '2:00 PM',
                endTime: '4:00 PM',
                duration: '2h',
                description: 'Code review and bug fixes',
                billable: false,
                loggedBy: 'John Doe',
                loggedAt: '2025-08-05 4:05 PM'
            },
            {
                id: 3,
                date: '2025-08-04',
                startTime: '10:00 AM',
                endTime: '12:00 PM',
                duration: '2h',
                description: 'Initial setup and configuration',
                billable: true,
                loggedBy: 'John Doe',
                loggedAt: '2025-08-04 12:10 PM'
            }
        ],
        comments: [
            {
                id: 1,
                comment: 'Task has been updated with new requirements',
                commentedBy: 'Jane Smith',
                commentedAt: '2025-08-06 10:15 AM',
                type: 'update'
            },
            {
                id: 2,
                comment: 'Priority changed from Medium to High',
                commentedBy: 'Project Manager',
                commentedAt: '2025-08-05 3:30 PM',
                type: 'priority'
            },
            {
                id: 3,
                comment: 'Task assigned to John Doe',
                commentedBy: 'Team Lead',
                commentedAt: '2025-08-04 9:00 AM',
                type: 'assignment'
            }
        ],
        activities: [
            {
                id: 1,
                activity: 'Task status changed to In Progress',
                performedBy: 'John Doe',
                performedAt: '2025-08-06 9:00 AM',
                type: 'status'
            },
            {
                id: 2,
                activity: 'Due date extended to August 8, 2025',
                performedBy: 'Project Manager',
                performedAt: '2025-08-05 4:30 PM',
                type: 'date'
            },
            {
                id: 3,
                activity: 'Task created',
                performedBy: 'Team Lead',
                performedAt: '2025-08-04 8:45 AM',
                type: 'created'
            }
        ]
    };

    const getTotalLoggedTime = () => {
        const total = historyData.timeLog.reduce((acc, log) => {
            const [hours, minutes] = log.duration.split(/[hm\s]/).filter(Boolean);
            return acc + (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
        }, 0);
        
        const totalHours = Math.floor(total / 60);
        const totalMinutes = total % 60;
        return `${totalHours}h ${totalMinutes}m`;
    };

    const getBillableTime = () => {
        const billable = historyData.timeLog
            .filter(log => log.billable)
            .reduce((acc, log) => {
                const [hours, minutes] = log.duration.split(/[hm\s]/).filter(Boolean);
                return acc + (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
            }, 0);
        
        const billableHours = Math.floor(billable / 60);
        const billableMinutes = billable % 60;
        return `${billableHours}h ${billableMinutes}m`;
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'update':
                return 'mt-1 fas fa-edit text-primary';
            case 'priority':
                return 'mt-1 fas fa-exclamation-triangle text-warning';
            case 'assignment':
                return 'mt-1 fas fa-user text-info';
            case 'status':
                return 'mt-1 fas fa-tasks text-success';
            case 'date':
                return 'mt-1 fas fa-calendar text-warning';
            case 'created':
                return 'mt-1 fas fa-plus-circle text-success';
            default:
                return 'mt-1 fas fa-info-circle text-muted';
        }
    };

    const sortData = (data) => {
        return [...data].sort((a, b) => {
            const dateA = new Date(a.loggedAt || a.commentedAt || a.performedAt);
            const dateB = new Date(b.loggedAt || b.commentedAt || b.performedAt);
            return sortBy === 'newest' ? dateB - dateA : dateA - dateB;
        });
    };

    return (
        <Offcanvas show={show} onHide={onHide} placement="end" className="md-offcanvas">
            <Offcanvas.Header className='px-4 py-3' closeButton>
                <Offcanvas.Title className='display-1'>Task History</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="p-0">
                {/* Navigation Tabs */}
                <div className="px-4 pb-3 bg-white position-sticky top-0">
                    <Nav variant="tabs" className="c-nav-tabs">
                        <Nav.Item>
                            <Nav.Link 
                                active={activeTab === 'timeLog'}
                                onClick={() => setActiveTab('timeLog')}
                            >
                                Time Logs
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link 
                                active={activeTab === 'comments'}
                                onClick={() => setActiveTab('comments')}
                            >
                                Comments
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link 
                                active={activeTab === 'activities'}
                                onClick={() => setActiveTab('activities')}
                            >
                                Activities
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                </div>

                {/* Content Area */}
                <div className="px-4 pb-3 tab-content-scroll">
                    {/* Time Log Tab */}
                    {activeTab === 'timeLog' && (
                        <div>
                            {sortData(historyData.timeLog).map((log) => (
                                <div key={log.id} className='border p-3 rounded-3 mb-3'>
                                    <div className='p-tag fs-12'>
                                        {log.date} at {log.startTime}
                                    </div>
                                    <div className='p-tag fw-bold border-bottom pb-2'>{log.loggedBy}</div>
                                    <div className='p-tag mt-2'>
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <strong>Duration:</strong> {log.duration}
                                            {log.billable && (
                                                <span className="text-success fw-semibold ms-1">Billable</span>
                                            )}
                                        </div>
                                        <div className='p-tag'>
                                            <strong>Time:</strong> {log.startTime} - {log.endTime}
                                        </div>
                                        {log.description && (
                                            <div className='p-tag mt-1'>
                                                <strong>Description:</strong> {log.description}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Comments Tab */}
                    {activeTab === 'comments' && (
                        <div>
                            {sortData(historyData.comments).map((comment) => (
                                <div key={comment.id} className='border p-3 rounded-3 mb-3'>
                                    <div className='p-tag fs-12'>
                                        {comment.commentedAt}
                                    </div>
                                    <div className='p-tag fw-bold border-bottom pb-2'>{comment.commentedBy}</div>
                                    <div className='p-tag mt-2'>{comment.comment}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Activities Tab */}
                    {activeTab === 'activities' && (
                        <div>
                            {sortData(historyData.activities).map((activity) => (
                                <div key={activity.id} className='border p-3 rounded-3 mb-3'>
                                    <div className='p-tag fs-12'>
                                        {activity.performedAt}
                                    </div>
                                    <div className='p-tag fw-bold border-bottom pb-2'>{activity.performedBy}</div>
                                    <div className='p-tag mt-2'>{activity.activity}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};
